// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useRootOrganizationStore from '../../../../store/rootOrganization';
import useSubscriptionStore from '@/app/store/modules/subscription';
import actionsSubscriptions from '../../api/actionsSubscriptions';
// Components
import DataTable from '../../../../../common-utils/components/renders/dataTable/DataTable';
// Common
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
import subscriptionFields from '../../fields/subscriptionFields';
import getInitialQuery from '@/common-utils/components/renders/dataTable/utils/getInitialQuery';
import mergeQuery from '@/common-utils/components/renders/dataTable/utils/mergeQuery';
import Permissions from '@/app/permissions/Permissions';
import { columnsSubscription, columnsSubscriptionId } from './columnDef/columnsSubscription';
import { SUBSCRIPTIONS } from '../../api/apiUrls';

/**
 * @param  root0
 * @param  root0.filtersEventName
 * @param  root0.customQuery
 * @param root0.rowsSelected
 * @param root0.setRowsSelected
 * @param root0.organizationId
 * @param root0.maxPicks
 * @param root0.pageActions
 * @returns {JSX} SubscriptionsTable
 */
function SubscriptionsTable({
    filtersEventName,
    customQuery = {},
    rowsSelected = undefined,
    setRowsSelected = undefined,
    organizationId = undefined,
    maxPicks,
    pageActions = undefined
}) {
    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);
    const { refetchSubscriptions: refetchFromStore, storeRefetchSubscriptions, query: queryStored, storeQuery } = useSubscriptionStore();

    // Constants
    const selectionMode = !!(rowsSelected && setRowsSelected);
    const QUERY_PRESETS = {
        filters: [
            ...(organizationId
                ? [{
                    key: generateNestedFieldAccessor(subscriptionFields.CLIENT.UUID),
                    value: organizationId,
                    keepOnReset: true,
                    hidden: true
                }]
                : []),
            ...customQuery.filters || []
        ]
    };
    const initialQuery = getInitialQuery(QUERY_PRESETS);

    // State
    const [query, setQuery] = useState(mergeQuery(initialQuery, queryStored));
    const [launchRefetch, setLaunchRefetch] = useState(true);

    // Api Calls
    const { data, isFetching, refetch } = actionsSubscriptions.getSubscriptions(query, false);

    // Hooks
    useEffect(() => {
        if (rootOrganization && (launchRefetch || refetchFromStore)) {
            refetch(true);
            setLaunchRefetch(false);
            storeRefetchSubscriptions(false);
        }
    }, [query, launchRefetch, refetchFromStore]);

    const allColumns = columnsSubscription({ query, setQuery, queryStored, storeQuery, setLaunchRefetch, selectionMode, maxPicks, filtersEventName });

    // Render
    return (
        <DataTable
            filtersEventName={filtersEventName}
            columns={allColumns}
            data={data}
            dataFetchingIsLoading={isFetching}
            columnsId={columnsSubscriptionId}
            initialQuery={initialQuery}
            query={query}
            setQuery={setQuery}
            queryStored={queryStored}
            storeQuery={storeQuery}
            setLaunchRefetch={setLaunchRefetch}

            selectionMode={selectionMode}
            rowsSelected={rowsSelected}
            setRowsSelected={setRowsSelected}

            pageActions={pageActions}
            exportProps={!organizationId ?
                {
                    permissions: [
                        Permissions.EXPORT_SUBSCRIPTIONS.name
                    ],
                    api: SUBSCRIPTIONS.EXPORT
                } : null
            }
        />
    );
}

SubscriptionsTable.propTypes = {
    filtersEventName: PropTypes.string,
    customQuery: PropTypes.object,
    rowsSelected: PropTypes.object,
    setRowsSelected: PropTypes.func,
    organizationId: PropTypes.string,
    maxPicks: PropTypes.number,
    pageActions: PropTypes.node
};

export default SubscriptionsTable;